import React, { useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { margin } from 'polished';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';

import { getBillingDetails } from 'pages/Settings/SettingsPage/SettingsPayment/utils';

import { TotemButton } from 'ui/Button';
import { TotemInput } from 'styles';
import { colors } from 'styles/theme';

import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import {
    GetOrganizationQuery,
    GetUserQuery,
    useCreateSetupIntentMutation,
    useSetStripeDefaultPaymentMethodMutation,
} from 'data/__generated__';

export interface CreditCardFormProps {
    currentUser: GetUserQuery['user'] | null;
    organization: GetOrganizationQuery['organizationOfUser'];
    onSubmitCompletion: () => void;
}

export const CreditCardForm: React.FC<CreditCardFormProps> = ({ organization, currentUser, onSubmitCompletion }) => {
    // inspired by https://github.com/stripe/react-stripe-js/blob/9fe1a5473cd1125fcda4e01adb6d6242a9bae731/examples/hooks/2-Split-Card.js
    const elements = useElements();
    const stripe = useStripe();
    const [isLoading, setIsLoading] = useState(false);
    const [createSetupIntent] = useCreateSetupIntentMutation();
    const [setStripeDefaultPaymentMethod] = useSetStripeDefaultPaymentMethodMutation();
    const { billingInfo } = useOrganizationBillingInfo();
    const [cardName, setCardName] = useState<string | undefined | null>(
        currentUser?.profile?.firstname &&
            currentUser?.profile?.lastname &&
            `${currentUser?.profile?.firstname} ${currentUser?.profile?.lastname}`,
    );
    const [errorMessage, setErrorMessage] = useState<string | undefined | null>(null);
    const ELEMENT_STYLES = {
        base: {
            color: colors.black,
            fontFamily: 'Aeonik',
            fontSize: '16px',
            margin: '14px',
            ':focus': {
                color: colors.black,
            },

            '::placeholder': {
                color: colors.pantinGrey,
            },
        },
        invalid: {
            color: '#fff',
            ':focus': {
                color: colors.red,
            },
            '::placeholder': {
                color: '#FFCCA5',
            },
        },
    };
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        event.preventDefault();

        // In e2e, Stripe is mocked server-side (no real SetupIntent) and Stripe.js cannot confirm a
        // mocked client secret. Skip the Stripe Elements flow and register a default payment method
        // directly — same env.E2E bypass pattern used for Intercom (see App.tsx / AccountMenu).
        if (window.__TOTENV__.E2E) {
            if (currentUser?._id) {
                await setStripeDefaultPaymentMethod({
                    variables: {
                        userId: currentUser._id,
                        organizationId: organization?._id,
                        paymentMethodId: 'pm_e2e_default',
                    },
                });
                onSubmitCompletion();
            }
            setIsLoading(false);
            return;
        }

        if (!stripe || !elements) {
            setIsLoading(false);
            return;
        }
        const cardElement = elements.getElement(CardNumberElement);
        track(analyticsEvents.ADD_CARD, {
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.CHECKOUT_BASKET,
        });

        const { data } = await createSetupIntent({
            variables: {
                organizationId: organization?._id,
            },
        });
        const clientSecret = data?.createSetupIntent?.clientSecret ?? '';
        if (!cardElement) {
            setErrorMessage('Card information is incomplete or invalid.');
            setIsLoading(false);
            return;
        }
        if (!stripe) {
            setErrorMessage('Stripe is not loaded.');
            setIsLoading(false);
            return;
        }
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                // type: STRIPE_CARD,
                card: cardElement,
                billing_details: getBillingDetails({ cardName, billingInfo, user: currentUser }),
            },
        });
        if (error) {
            setErrorMessage(error?.message);
            setIsLoading(false);
            return;
        } else if (setupIntent.status === 'succeeded') {
            if (!currentUser?._id || !setupIntent?.payment_method) {
                // Sortie muette auparavant : le bouton restait bloqué en chargement, sans message ni
                // moyen de réessayer, alors que Stripe a bien enregistré le moyen de paiement.
                setErrorMessage(
                    "Le moyen de paiement a bien été enregistré mais n'a pas pu être sélectionné. Rechargez la page.",
                );
                setIsLoading(false);
                return;
            }
            const paymentMethodId =
                typeof setupIntent.payment_method === 'string'
                    ? setupIntent.payment_method
                    : setupIntent.payment_method?.id;
            setStripeDefaultPaymentMethod({
                variables: {
                    userId: currentUser?._id,
                    organizationId: organization?._id,
                    paymentMethodId,
                },
            });
            onSubmitCompletion();
        }
        setIsLoading(false);
    };

    return (
        <CreditCardFormContainer onSubmit={handleSubmit}>
            <CardNameInput
                placeholder="Titulaire de la carte"
                value={cardName ?? ''}
                onChange={(e) => setCardName(e.target.value)}
            />
            <ElementWithTitleContainer $margins={[1, 0, 2, 1]}>
                <CardNumberElement options={{ style: ELEMENT_STYLES }} />
            </ElementWithTitleContainer>
            <Line>
                <ElementWithTitleContainer $margins={[0, 0, 0, 1]}>
                    <CardExpiryElement options={{ style: ELEMENT_STYLES }} />
                </ElementWithTitleContainer>
                <ElementWithTitleContainer $margins={[0, 0, 0, 1]}>
                    <CardCvcElement options={{ style: ELEMENT_STYLES }} />
                </ElementWithTitleContainer>
            </Line>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <TotemButton disabled={isLoading} $margins={[2, 0, 0, 0]} $fontSize="1em" type="submit">
                {isLoading ? <ClipLoader color="white" size="16px" /> : 'Enregistrer la carte'}
            </TotemButton>
        </CreditCardFormContainer>
    );
};

const CreditCardFormContainer = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 1em 0 0 0;
`;

const Line = styled.div`
    display: flex;
    flex-direction: row;
`;

const ErrorMessage = styled.div`
    margin: 1em 1em 0 1em;
    color: ${({ theme: { colors } }) => colors.red};
`;
type ElementWithTitleContainerProps = {
    $margins?: number[];
};
const ElementWithTitleContainer = styled.div<ElementWithTitleContainerProps>`
    ${({ $margins }) =>
        $margins &&
        margin(
            $margins[0] + 'rem',
            ($margins[1] ?? $margins[0]) + 'rem',
            ($margins[2] ?? $margins[0]) + 'rem',
            ($margins[3] ?? $margins[1] ?? $margins[0]) + 'rem',
        )}
    width: 100%;
`;

const CardNameInput = styled(TotemInput)`
    border: none;
    margin: 0;
    &:hover {
        border: none;
    }
    &:active,
    &:focus {
        border: none;
        box-shadow: none;
    }
    color: #111111;
    font-family: Aeonik;
    font-size: 16px;
    outline: none;
`;
